import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { PandaDocAnalyticsData } from 'core/hooks';

import * as S from '../PandaDocAnalytics.styled';
import { COLORS, Granularity } from '../constants';
import { ChartCard } from '../components';
import { HorizontalBarChart, StackedBarChart } from '../charts';
import { DASH } from '../utils';

interface ICoursesTabProps {
  data: PandaDocAnalyticsData;
  granularity: Granularity;
}

/** Cross-course comparison. The course filter is ignored here by design. */
export const CoursesTab = memo(({ data, granularity }: ICoursesTabProps) => {
  const { t } = useTranslation();
  const ttsByCourse = (data.ttsByCourse ?? []).filter((c): c is NonNullable<
    typeof c
  > => Boolean(c));
  const conversionByCourse = (
    data.conversionByCourse ?? []
  ).filter((c): c is NonNullable<typeof c> => Boolean(c));

  return (
    <>
      <S.SectionTitle>
        {t('pages.pandadocAnalytics.courses.title')}
      </S.SectionTitle>
      <S.FooterNote style={{ marginTop: 0, marginBottom: 8 }}>
        {t('pages.pandadocAnalytics.courses.footer')}
      </S.FooterNote>

      <ChartCard
        title={t('pages.pandadocAnalytics.courses.avgTtsTitle')}
        meta={t('pages.pandadocAnalytics.courses.avgTtsMeta')}
      >
        <HorizontalBarChart
          labels={ttsByCourse.map((c) => c.course ?? DASH)}
          // Empty courses report avgDays=0 from the API — render a gap, not a
          // misleading "0 дн" bar.
          values={ttsByCourse.map((c) =>
            c.sample && c.sample > 0 ? c.avgDays ?? null : null,
          )}
        />
      </ChartCard>

      <ChartCard
        title={t('pages.pandadocAnalytics.courses.conversionTitle')}
        tall
        legend={
          <S.Legend>
            <span>
              <S.Dot color={COLORS.signed} />
              {t('pages.pandadocAnalytics.charts.signed')}
            </span>
            <span>
              <S.Dot color={COLORS.active} />
              {t('pages.pandadocAnalytics.charts.active')}
            </span>
            <span>
              <S.Dot color={COLORS.lost} />
              {t('pages.pandadocAnalytics.charts.lost')}
            </span>
          </S.Legend>
        }
        footer={t('pages.pandadocAnalytics.courses.conversionFooter')}
      >
        <StackedBarChart
          horizontal
          labels={conversionByCourse.map((c) => c.course ?? DASH)}
          signed={conversionByCourse.map((c) => c.signed ?? 0)}
          active={conversionByCourse.map((c) => c.active ?? 0)}
          lost={conversionByCourse.map((c) => c.lost ?? 0)}
          granularity={granularity}
        />
      </ChartCard>
    </>
  );
});
